/**
 * Copyright (c) 2025, CheckPlus Limited
 */

// Avatar tile colours. An organisation that has not set a brand colour gets
// one of these from a hash of its id — decoration only. The labels are used by
// the (mock) donor impact report's cause split and nowhere else; no real
// organisation is ever described by one, because the Organization schema has
// no cause field.
export const CATS = [
  { id: "homeless", label: "Homelessness", color: "#2b91d6", tint: "#e6f5ff", blurb: "Shelter, outreach and case work for people sleeping rough or living in subdivided flats across Hong Kong." },
  { id: "poverty", label: "Poverty & Hunger", color: "#45b58a", tint: "#e8f7f1", blurb: "Food rescue, meal programmes and cash assistance for low-income households." },
  { id: "disability", label: "Disability", color: "#175b89", tint: "#e7eef3", blurb: "Independent living, accessible employment and family support for disabled people." },
  { id: "youth", label: "Youth & Education", color: "#007ec1", tint: "#e4f2fa", blurb: "Tutoring, mentoring and after-school places for students who need them most." },
  { id: "mental", label: "Mental Health", color: "#41b2fd", tint: "#e9f6ff", blurb: "Counselling, crisis lines and peer support, free at the point of need." },
  { id: "elderly", label: "Elderly Care", color: "#2482c4", tint: "#e6f1f9", blurb: "Home visits, meals and companionship for older people living alone." },
  { id: "environment", label: "Environment", color: "#3aa47f", tint: "#e8f5f0", blurb: "Shoreline restoration, waste reduction and urban biodiversity work." },
  { id: "animals", label: "Animal Welfare", color: "#6fb2dc", tint: "#eaf4fb", blurb: "Rescue, veterinary care and rehoming for abandoned animals." },
];

// Small deterministic string hash (not cryptographic) so a given org id
// always derives the same placeholder profile across renders/reloads.
function hashString(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

// Real orgs from the DB carry name/subDomain/logo/verification, plus real
// raised/backer figures aggregated from Project/Payment records. Nothing else
// is invented on top: an organisation has no cause, district or description on
// its schema, so the marketplace shows none. Only two derived values remain,
// both explained where they are used — an avatar colour and an annual goal.
export function buildOrgProfile(org, index) {
  const hash = hashString(org.id || String(index));
  // The discover card shows progress against an annual funding goal. There is
  // no goal field on the schema, so it is derived from what the org has
  // actually raised — deterministic, and always a target it is short of rather
  // than an invented figure.
  const goalRatio = 1.15 + (hash % 60) / 100;
  const goal = Math.max(200000, Math.round((org.raised || 0) * goalRatio) || 200000);
  return {
    id: org.id,
    // The organisation's own numeric id. Payments carry it as
    // organizationViewId (verify.js sets session.viewId = user.id, and a
    // project stores that), so it is the only key that ties a donor's payment
    // back to the nonprofit that received it — `id` above is the subdomain for
    // most orgs and cannot be matched against a payment (CP-285).
    viewId: org.viewId,
    name: org.name,
    logo: org.logo || "",
    headerColor: org.headerColor || "",
    verified: org.verified,
    // Palette bucket for the avatar tile, not a statement about the work.
    cat: CATS[hash % CATS.length].id,
    raised: org.raised || 0,
    goal,
    goalPct: Math.min(99, Math.round(((org.raised || 0) / goal) * 100)),
    backers: (org.backers || 0).toLocaleString("en-US"),
    // What a day of frontline work costs, behind "what your giving buys".
    // Placeholder: no organisation publishes a unit cost yet.
    dayCost: 50 + (hash % 10) * 10,
    // Whether this org can actually take a real charge (Stripe Connect
    // payout destination ready). Orgs with an explicit false cannot be added
    // to a giving split (CP-268 / CP-304). Missing defaults to ready so
    // existing directory entries keep working until the backend always sends
    // the flag; unready Plug signups are omitted or flagged false by the API.
    paymentReady: org.paymentReady !== false,
  };
}

// Marketplace-wide headline figures. Placeholder — there is no aggregate
// endpoint for these yet, unlike verifiedOrgCount which is counted from the
// real directory (see CP-228).
export const HERO_STATS = { givenThisYear: "HK$8.4M", medianGift: "HK$180" };

// Public (signed-out) impact page. Network-wide figures and the unit costs
// behind the "what would your giving buy" estimator. All placeholder — there
// is no aggregate endpoint for any of it yet (CP-228).
export const PUBLIC_IMPACT = {
  stats: [
    { id: "given", value: "HK$8.4M" },
    { id: "orgs", value: "142" },
    { id: "biz", value: "384" },
    { id: "cut", value: "HK$0" },
  ],
  // Unit cost per outcome, and the share of a plan that typically goes there.
  tryRows: [
    { id: "shelter", share: 0.4, unit: 120 },
    { id: "meals", share: 0.35, unit: 25 },
    { id: "therapy", share: 0.25, unit: 700 },
  ],
  funded: [
    { id: "nights", value: "21,400", color: "#2b91d6" },
    { id: "meals", value: "468,000", color: "#45b58a" },
    { id: "counselling", value: "3,100", color: "#41b2fd" },
    { id: "employment", value: "640", color: "#175b89" },
  ],
};

// Donor/business impact report. Amounts, milestones and receipts are mock —
// there is no per-donor giving history in the backend yet. The outcome rows
// are remapped onto real orgs at render time, the same way BIZ.supports is.
export const IMPACT = {
  totalGiven: 2400,
  totalGivenBiz: 184000,
  months: 11,
  bizMultiplier: 76.7,
  causeSplit: [
    { cat: "homeless", pct: 40 },
    { cat: "poverty", pct: 35 },
    { cat: "mental", pct: 25 },
  ],
  outcomes: [{ amount: 960 }, { amount: 840 }, { amount: 600 }],
  ledger: [
    { date: "1 Aug 2026", amount: 200, ref: "RC-2608" },
    { date: "1 Jul 2026", amount: 200, ref: "RC-2607" },
    { date: "1 Jun 2026", amount: 200, ref: "RC-2606" },
    { date: "1 May 2026", amount: 200, ref: "RC-2605" },
  ],
  bizExtra: { customers: "38,400", receiptsShown: "31,900", staffPicks: 2 },
};

// A giving rule is one of three shapes. Labels and hints live in the locale
// files; only the shape is structural.
export const RULE_TYPES = ["revenue", "sale", "budget"];

export const PRESETS = [100, 200, 500, 1000];

// Individual giving is chosen in two steps: one-time or recurring, and then
// how often the recurring gift repeats. `freq` holds "once" or one of these.
// The amount entered is per period, so a yearly plan divides by 12 to show a
// monthly figure and a quarterly one divides by 3.
export const FREQ_CADENCES = ["monthly", "quarterly", "annual"];

export const INDUSTRIES = ["Food & drink", "Retail", "Services", "Health & fitness", "Professional"];

// Mock business directory behind the giving league, the nonprofit's supporter
// list and the charity-sale listings. `rule` is structured rather than a
// sentence so it can be phrased in each language; `supports` is remapped onto
// real org ids once the directory has loaded.
export const BIZ = [
  { id: "lungcheong", name: "Lung Cheong Bakery", industry: "Food & drink", district: "Sham Shui Po", rule: { type: "revenue", pct: 2 }, given: 184000, since: 2021, growth: "+31%", supports: ["rescue", "twodollar", "harbour"] },
  { id: "kadoorie", name: "Kadoorie Coffee Roasters", industry: "Food & drink", district: "Yau Ma Tei", rule: { type: "sale", pct: 1.5 }, given: 96000, since: 2022, growth: "+27%", supports: ["nightlight", "quiet"] },
  { id: "peakcycles", name: "Peak Cycles", industry: "Retail", district: "Wan Chai", rule: { type: "revenue", pct: 1 }, given: 142000, since: 2020, growth: "+12%", supports: ["youthlab", "lantau"] },
  { id: "swbooks", name: "Sheung Wan Books", industry: "Retail", district: "Sheung Wan", rule: { type: "budget", amount: 6000 }, given: 288000, since: 2019, growth: "+6%", supports: ["chapter", "teatime"] },
  { id: "ktprint", name: "Kwun Tong Print Co.", industry: "Services", district: "Kwun Tong", rule: { type: "revenue", pct: 2.5 }, given: 312000, since: 2021, growth: "+24%", supports: ["brightsteps", "signal"] },
  { id: "harbouryoga", name: "Harbour Yoga", industry: "Health & fitness", district: "Causeway Bay", rule: { type: "revenue", pct: 3 }, given: 74000, since: 2023, growth: "+38%", supports: ["quiet", "openline"] },
  { id: "namcheong", name: "Nam Cheong Grocers", industry: "Food & drink", district: "Sham Shui Po", rule: { type: "sale", pct: 1 }, given: 122000, since: 2022, growth: "+21%", supports: ["rescue", "golden"] },
  { id: "islanddental", name: "Island Dental Studio", industry: "Health & fitness", district: "Central", rule: { type: "budget", amount: 10000 }, given: 420000, since: 2019, growth: "+18%", supports: ["brightsteps", "harbour", "golden"] },
  { id: "tsimoptical", name: "Tsim Sha Tsui Optical", industry: "Retail", district: "Tsim Sha Tsui", rule: { type: "revenue", pct: 1.5 }, given: 88000, since: 2023, growth: "+15%", supports: ["signal", "chapter"] },
  { id: "wanchaiwine", name: "Wan Chai Wine Merchants", industry: "Retail", district: "Wan Chai", rule: { type: "revenue", pct: 1 }, given: 156000, since: 2020, growth: "+9%", supports: ["lantau", "paws"] },
  { id: "moraccount", name: "Mor & Chan Accountants", industry: "Professional", district: "Admiralty", rule: { type: "revenue", pct: 2 }, given: 264000, since: 2021, growth: "+2%", supports: ["openline", "youthlab"] },
  { id: "pawsgroom", name: "Tai Po Grooming Rooms", industry: "Services", district: "Tai Po", rule: { type: "sale", pct: 4 }, given: 52000, since: 2024, growth: "+44%", supports: ["paws"] },
];

// The nonprofit workspace's own figures. Placeholder — a signed-in
// organisation's real month, payout and supporter mix all need endpoints that
// do not exist yet (CP-228).
export const NP = {
  received: 142800,
  recurring: 96400,
  oneTime: 46400,
  individuals: 88536,
  businesses: 54264,
  indGivers: "3,240",
  bizGivers: 84,
  newThisMonth: 186,
  churn: 24,
  payoutDate: "1 September",
  lastPayouts: [
    { date: "1 Aug 2026", amount: 138200, ref: "PO-2608" },
    { date: "1 Jul 2026", amount: 129450, ref: "PO-2607" },
    { date: "1 Jun 2026", amount: 133900, ref: "PO-2606" },
  ],
  // `rec` is the recurring part of each month, drawn as the solid inner bar.
  trend: [
    { m: "Sep", value: 84, rec: 61 }, { m: "Oct", value: 91, rec: 66 },
    { m: "Nov", value: 96, rec: 70 }, { m: "Dec", value: 128, rec: 78 },
    { m: "Jan", value: 104, rec: 74 }, { m: "Feb", value: 99, rec: 72 },
    { m: "Mar", value: 112, rec: 80 }, { m: "Apr", value: 118, rec: 84 },
    { m: "May", value: 121, rec: 88 }, { m: "Jun", value: 134, rec: 92 },
    { m: "Jul", value: 129, rec: 94 }, { m: "Aug", value: 143, rec: 96 },
  ],
  // `who` is a display name and `bizIndex` points into BIZ for the business
  // rows; everything else is an id the locale phrases — kind is
  // individual/business/anon, mode the giving arrangement, when the recency
  // bucket.
  recent: [
    { id: "r1", who: "Tania L.", kind: "individual", mode: "monthly", amount: 80, share: 40, when: "hours", n: 2 },
    { id: "r2", bizIndex: 0, kind: "business", mode: "revenue", amount: 14200, share: 45, when: "yesterday" },
    { id: "r3", who: "", kind: "anon", mode: "once", amount: 500, when: "yesterday" },
    { id: "r4", who: "Marcus C.", kind: "individual", mode: "monthly", amount: 120, share: 60, when: "days", n: 2 },
    { id: "r5", bizIndex: 7, kind: "business", mode: "budget", amount: 10000, share: 33, when: "days", n: 3 },
    { id: "r6", who: "Priya S.", kind: "individual", mode: "monthly", amount: 45, share: 25, when: "days", n: 4 },
  ],
};

// ---------------------------------------------------------------------------
// Layout v2 datasets.
//
// v2 gives each account type its own workspace, and every workspace carries a
// "you", a "them" and a "peers" view. Only the nonprofit directory behind
// those views is real (GET /marketplace/nonprofits); every figure below is
// placeholder, exactly like NP/BIZ/IMPACT above — there is no supporter,
// outcome, product or campaign endpoint yet (CP-228). Wherever a row names a
// nonprofit or a business it is remapped onto the fetched directory at render
// time rather than naming an organisation we have not verified.
// ---------------------------------------------------------------------------

// Nonprofit · impact and proof.
// The key metrics, the giving-buys rows and the What's new feed used to be
// four invented arrays here (OUTCOMES, UPDATES, BIG_METRICS and
// METRIC_PERIOD). They are now the organisation's own rows, read from
// GET /organizations/impact — a page whose subject is proof cannot ship with
// placeholder proof on it. What is left below is UI vocabulary: the templates
// offered, what can be attached, and how a number can be written.

export const UPDATE_KINDS = ["photo", "video", "report", "numbers"];

// Nonprofit · publishing. Part 1 is the key metrics, part 2 is what a giving
// buys, part 3 is the What's new feed. Templates are offered first in both
// forms because a named row is easier to publish than a blank field.
export const METRIC_TEMPLATES = [
  "mission",
  "admin",
  "peopleHelped",
  "provided",
  "movedOn",
  "campaign",
];

// How a number reads. The value itself is always stored as a plain number so
// it can be reformatted or compared; this is only the presentation.
export const VALUE_FORMATS = ["number", "percent", "currency"];

// The format a template's number almost always wants, so picking "% of
// funding spent on the mission" does not then need the organisation to also
// say "percent". Only a starting point — the selector stays editable.
export const METRIC_TEMPLATE_FORMATS = {
  mission: "percent",
  admin: "percent",
};

// Part 2 · the examples offered for "what a giving buys". The amount is a
// plain number for the same reason a metric's value is: it is rendered as
// "HK$600", but a stored "HK$600" could never be compared or reformatted.
export const BUY_TEMPLATES = [
  { id: "foodpack", amount: 350, iconId: "food" },
  { id: "bednights", amount: 600, iconId: "bed" },
  { id: "casework", amount: 1200, iconId: "people" },
  { id: "training", amount: 2500, iconId: "work" },
  { id: "deposit", amount: 5000, iconId: "home" },
];

// The icon set offered beside the emoji field. Ids are what the backend
// stores and validates against; the paths never leave the client.
export const ICONS = [
  { id: "bed", path: "M3 18v-6h11a4 4 0 0 1 4 4v2M3 18h18M3 12V7m4 3h3" },
  { id: "food", path: "M6 8h12l-1.2 12H7.2L6 8zm3-.5V6a3 3 0 0 1 6 0v1.5" },
  {
    id: "work",
    path: "M3 8h18v11H3zM8.5 8V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v2M3 13h18",
  },
  { id: "home", path: "M4 11l8-7 8 7v9H4v-9zm5 9v-6h6v6" },
  {
    id: "coin",
    path:
      "M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18zm0 4v10m2.5-7.5H10.8a1.7 1.7 0 0 0 " +
      "0 3.4h2.4a1.7 1.7 0 0 1 0 3.4H9.5",
  },
  { id: "chart", path: "M4 20V11m5 9V5m5 15v-6m5 6V8M3 20h18" },
  {
    id: "heart",
    path: "M12 20s-7-4.4-7-9a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 4.6-7 9-7 9z",
  },
  {
    id: "people",
    path:
      "M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7 0a2.5 2.5 0 1 0 0-5M3 20v-1.5A4.5 " +
      "4.5 0 0 1 7.5 14h3A4.5 4.5 0 0 1 15 18.5V20m4 0v-1.5a4 4 0 0 0-2.5-3.7",
  },
  {
    id: "shield",
    path: "M12 3l7 3v5c0 5-3.2 8.3-7 10-3.8-1.7-7-5-7-10V6l7-3zm-2.5 9l2 2 3.5-4",
  },
];

// How often a fundraising goal repeats. One goal per organisation — this
// describes that goal recurring, not several running at once.
export const GOAL_CADENCES = ["annual", "quarterly", "monthly", "one-time"];

// The hosts and shape /organizations/impact accepts. Kept in step with
// parseYoutubeId there: the preview must resolve exactly what the server will
// store, or a link previews here and is refused on save.
const YOUTUBE_HOSTS = [
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
  "www.youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
];

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/**
 * The 11-character video id inside whatever was pasted.
 *
 * Only an id ever reaches an iframe src, here as on the server — the host is
 * checked against the list above and the id's shape enforced, so a pasted
 * string cannot become a URL the page loads.
 *
 * @param {String} raw - A pasted link, or a bare id
 * @return {String} The id, or "" when the input is not a YouTube link
 */
export const parseYoutubeId = (raw) => {
  // Capped the way the server caps it before parsing, so the two agree on
  // what a link is rather than differing on an over-long paste.
  const value = (raw || "").trim().slice(0, 200);
  if (!value) {
    return "";
  }
  if (YOUTUBE_ID.test(value)) {
    return value;
  }
  let url;
  try {
    url = new URL(value);
  } catch (err) {
    return "";
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return "";
  }
  if (YOUTUBE_HOSTS.indexOf(url.hostname.toLowerCase()) === -1) {
    return "";
  }
  const segments = url.pathname.split("/").filter(Boolean);
  let id = null;
  if (url.hostname.toLowerCase().endsWith("youtu.be")) {
    id = segments[0] || null;
  } else if (url.pathname === "/watch") {
    id = url.searchParams.get("v");
  } else if (["embed", "shorts", "live", "v"].indexOf(segments[0]) !== -1) {
    id = segments[1] || null;
  }
  return id && YOUTUBE_ID.test(id) ? id : "";
};

// Part 3 · what can be attached to an update post.
export const POST_ATTACHMENTS = ["photo", "video", "file"];

export const ASKS = ["rule", "report", "budget", "eyetests"];

// Nonprofit · our supporters, individual half of the panel.
export const SUPPORTER_STATS = { monthly: "3,240", median: "HK$27", newThisMonth: "+186", share: "38%" };

// Business · sales and giving. Charity-sale lines sit on top of the rule.
export const CHARITY_TOTAL = 35762;

export const PRODUCTS = [
  { id: "dripbag", cut: 10, orders: "742", sales: 118720, giving: 11872, lift: 68 },
  { id: "beans", cut: 8, orders: "486", sales: 155520, giving: 12442, lift: 41 },
  { id: "cake", cut: 12, orders: "318", sales: 95400, giving: 11448, lift: 52 },
];

export const OTHER_PRODUCT = { orders: "196", sales: 54300, giving: 815 };

export const BIZ_SALES = {
  revenue: 642700,
  orders: "1,842",
  charityOrders: "1,546",
  sinceTotal: 96000,
  rank: 4,
  badgeConvert: "4.1%",
  badgeBaseline: "3.2%",
  badgeBasket: "HK$38",
  trend: [
    { m: "Sep", sales: 402, giving: 60 }, { m: "Oct", sales: 388, giving: 58 },
    { m: "Nov", sales: 441, giving: 66 }, { m: "Dec", sales: 512, giving: 77 },
    { m: "Jan", sales: 468, giving: 70 }, { m: "Feb", sales: 455, giving: 68 },
    { m: "Mar", sales: 498, giving: 75 }, { m: "Apr", sales: 524, giving: 79 },
    { m: "May", sales: 561, giving: 84 }, { m: "Jun", sales: 588, giving: 88 },
    { m: "Jul", sales: 604, giving: 91 }, { m: "Aug", sales: 643, giving: 96 },
  ],
};

// Shape of the month-by-month bars inside an expanded giving-league row.
export const LEAGUE_SPARK = [0.4, 0.46, 0.52, 0.49, 0.58, 0.55, 0.63, 0.68, 0.72, 0.79, 0.85, 0.94];

// Individual · businesses giving back. Charity-sale listings, and the shops
// funding the causes already in the plan.
export const CAMPAIGNS = [
  { id: "mooncake", price: 268, give: 54, raised: 46200, goal: 75000, pct: 62 },
  { id: "tuneup", price: 480, give: 180, raised: 28900, goal: 60000, pct: 48 },
  { id: "shoreline", price: 980, give: 120, raised: 61400, goal: 70000, pct: 88 },
];

export const BACKERS = [14200, 10000, 9640, 5400, 4900];

export const HISTORY = [
  { id: "jul", amount: 200 },
  { id: "jun", amount: 200 },
  { id: "may", amount: 150 },
  { id: "apr", amount: 150 },
];

// Sections per account type, in sidebar order. The first entry is where an
// account lands when it signs in or the account type changes.
export const SECTIONS = {
  // "profile" is drawn for all three personas, and the section itself is
  // persona-agnostic — it renders whichever parts are registered for the
  // workspace on screen, and each part reports nothing when the persona has
  // nothing for it. All three carry it now that all three have a catalogue.
  nonprofit: ["dash", "impact", "supporters", "peers", "profile"],
  // "inbox" is Nonprofit news, and it is the one section the nonprofit does
  // not get: it is assembled from who the reader follows, so a nonprofit
  // shown it would be reading which other nonprofits its supporters follow.
  business: ["sales", "discover", "portfolio", "board", "inbox", "profile"],
  // "dash" reads as "My dashboard" here and renders a donor's own giving, not
  // the nonprofit dashboard of the same id — see renderSection.
  individual: ["dash", "discover", "portfolio", "brands", "inbox", "profile"],
};

/**
 * Nonprofit news.
 *
 * One page of the feed, matching the server's own default. `Load more`
 * appends — the same pattern the updates feed on Impact & proof uses, rather
 * than a second kind of pagination for the same kind of list.
 */
export const NEWS_PAGE = 20;

/**
 * The unread badge's cap.
 *
 * Presentation, not accuracy: the number itself is whatever the server said,
 * and this is only how much of it fits in a pill beside a nav label. Kept
 * here rather than inline so the badge and anything that later explains it
 * agree on where the ceiling is.
 */
export const NEWS_BADGE_MAX = 9;

/*
 * A post's `kind` is not listed here on purpose. It is optional on
 * ImpactUpdate — a post that is just words is none of the four — and the
 * chips are keyed by it in m.news.chips, so the locale pack is already the
 * one list of them. A copy here would be a second list to keep in step with
 * the model's own enum.
 *
 * What every read of it does have to do is survive null: the chip falls back
 * to a neutral "UPDATE" rather than guessing from whatever the post happens
 * to carry, because a post with one photo attached is not necessarily a
 * photo story and mislabelling it is worse than not labelling it.
 */


/**
 * Profile questions, per account type (CP-228).
 *
 * Ids and order live here; the wording lives in the three locale files
 * under m.profileQuestions — the same split METRIC_TEMPLATES already uses,
 * and for the same reason. A question is Plug's words, so it translates
 * there; an answer is the account's choice, so the server stores only these
 * ids and never the labels.
 *
 * That split is what makes the answers usable. The profile page tells a
 * nonprofit these decide "how often you appear in the right search", so the
 * stored form has to be queryable; a stored label would be neither
 * queryable across languages nor safe to retranslate.
 *
 * `kind` is "multi", "single" or "text". `max` caps a multi-select where the
 * question says it caps one.
 *
 * `req` and `search` are presentation, not validation: `req` puts the
 * artboard's Required badge on a question during onboarding, and `search`
 * says the option list is long enough to be worth a search box rather than a
 * wall of chips. Neither blocks a save -- every question on this page can be
 * left unanswered, on the profile and in onboarding alike.
 *
 * All three personas are written. They deliberately do not share question
 * ids across personas even where the words nearly rhyme: `ownerType` is the
 * catalogue namespace on ProfileAnswer, so a nonprofit's "causes" (the work
 * it does) and a business's "causes" (the work it wants to fund) are two
 * questions that happen to have one id, and reading either across personas
 * would be a category error rather than a wider result set.
 */
export const PROFILE_QUESTIONS = {
  nonprofit: [
    // Asked and answered here like any other question, and deliberately not
    // prefilled from the name on file: the name a nonprofit wants donors to
    // see is not always the one its Section 88 registration is under, and a
    // field that arrives already filled is one nobody reads.
    //
    // It does mean two names can now disagree. Which one the public page
    // shows is a decision above this file — see the note in profileVm.
    { id: "orgname", kind: "text", req: true },
    {
      id: "causes",
      kind: "multi",
      req: true,
      search: true,
      options: [
        "children", "education", "elderly", "poverty", "homelessness",
        "health", "mental", "disability", "women", "environment",
        "animals", "community", "humanitarian", "arts", "other",
      ],
    },
    {
      id: "serve",
      kind: "multi",
      options: [
        "children", "youth", "elderly", "families", "lowIncome",
        "homeless", "disability", "patients", "mentalHealth", "women",
        "minorities", "communities", "animals", "environment", "disaster",
        "other",
      ],
    },
    {
      id: "where",
      kind: "multi",
      options: [
        "hongKong", "mainland", "macau", "taiwan", "seAsia", "otherAsia",
        "africa", "europe", "northAmerica", "latinAmerica", "oceania",
      ],
    },
    {
      // Capped at two, unlike the four questions above it. Those describe
      // what the organisation is and where it works, so a long answer is a
      // fuller answer; this one is a request put to businesses, and a
      // nonprofit that asks for all seven formats has stated no preference
      // for the matching to act on.
      id: "partner",
      kind: "multi",
      max: 2,
      options: [
        "donations", "revenueShare", "causeProducts", "sponsorship",
        "marketing", "longTerm", "other",
      ],
    },
    {
      id: "sdg",
      kind: "multi",
      search: true,
      options: [
        "sdg1", "sdg2", "sdg3", "sdg4", "sdg5", "sdg6", "sdg7", "sdg8",
        "sdg9", "sdg10", "sdg11", "sdg12", "sdg13", "sdg14", "sdg15",
        "sdg16", "sdg17", "unsure",
      ],
    },
  ],

  business: [
    // The name customers see, asked rather than taken from the organisation
    // record for the same reason the nonprofit's is: a brand name and a
    // registered company name are routinely not the same string, and the
    // storefront badge carries the first one.
    { id: "bizname", kind: "text", req: true },
    // Single choice, because it is what the peer benchmark groups by — a
    // business in two industries would be in two cohorts and comparable to
    // neither.
    {
      id: "industry",
      kind: "single",
      req: true,
      search: true,
      options: [
        "fashion", "beauty", "food", "retail", "hospitality", "finance",
        "professional", "technology", "education", "healthcare", "media",
        "property", "manufacturing", "logistics", "other",
      ],
    },
    // The same fifteen the nonprofit picks from, deliberately. Matching a
    // business to a nonprofit is an intersection of these two answers, and
    // an intersection needs one vocabulary — a business choosing from its
    // own list of causes would match nothing.
    //
    // Capped, unlike the nonprofit's: a business that funds everything is
    // asking for the whole directory back, which is the shortlist not
    // working. A nonprofit's causes describe what it is, so they are not
    // capped.
    {
      id: "causes",
      kind: "multi",
      max: 5,
      search: true,
      options: [
        "children", "education", "elderly", "poverty", "homelessness",
        "health", "mental", "disability", "women", "environment",
        "animals", "community", "humanitarian", "arts", "other",
      ],
    },
    {
      id: "goals",
      kind: "multi",
      max: 2,
      options: [
        "impact", "engage", "brand", "sales", "esg", "partnerships", "other",
      ],
    },
    {
      id: "budget",
      kind: "single",
      options: [
        "under10k", "to50k", "to100k", "to500k", "to1m", "over1m", "unsure",
      ],
    },
    {
      id: "customers",
      kind: "multi",
      options: [
        "genZ", "youngProfessionals", "families", "middleAged", "seniors",
        "premium", "massMarket", "b2b", "mixed", "other",
      ],
    },
    {
      // Two, because the answer picks the setup that follows -- choosing a
      // Plug shopping page is what a Stripe connected account gets created
      // for. Every route at once is not a path through onboarding.
      id: "useplug",
      kind: "multi",
      max: 2,
      options: [
        "addCsr", "plugStore", "directDonations", "multiNonprofit",
        "partnerships", "reporting", "unsure",
      ],
    },
  ],

  individual: [
    // Who a donor wants to help, in the nonprofit's own "who we support"
    // vocabulary rather than in the fifteen-cause one. A donor thinks in
    // people, not in sector labels, and matching against `serve` is the
    // read this answer is for.
    // Not `req`, and neither is anything else here. The artboard marks every
    // question a donor is asked as optional — a nonprofit is proving itself
    // and a business is publishing a rule, but a donor is only being asked
    // what they like, and a Required badge on that reads as a demand.
    {
      id: "causes",
      kind: "multi",
      max: 5,
      search: true,
      options: [
        "children", "youth", "elderly", "families", "lowIncome",
        "homeless", "disability", "patients", "mentalHealth", "women",
        "minorities", "communities", "animals", "environment", "disaster",
        "other",
      ],
    },
    {
      // Two out of six, the same cap the business's "what would you like
      // Plug to help you achieve" carries: this is a preference the feed is
      // built from, and a donor who picks everything has ranked nothing.
      id: "how",
      kind: "multi",
      max: 2,
      options: [
        "recurring", "multiNonprofit", "shopBrands", "causeProducts",
        "follow", "share",
      ],
    },
    {
      id: "budget",
      kind: "single",
      options: [
        "under50", "to100", "to300", "to500", "to1000", "over1000",
      ],
    },
    // The four below are the only questions in the whole catalogue that ask
    // about the person rather than about their giving, so every one of them
    // carries a "prefer not to say" option and none of them is `req`. The
    // meter counts them, which is why "prefer not to say" is a stored answer
    // rather than a skip: declining to answer is an answer, and a donor who
    // declines all four should still be able to reach a finished profile.
    {
      id: "age",
      kind: "single",
      options: [
        "under18", "to24", "to34", "to44", "to54", "to64", "over65", "noSay",
      ],
    },
    {
      id: "gender",
      kind: "single",
      options: ["female", "male", "other", "noSay"],
    },
    // Twelve and thirteen options, and no `search` on either: the artboard
    // draws both as chips, and a dozen short labels are read at a glance in a
    // way seventeen SDGs are not. `search` earns its place on a list somebody
    // would otherwise scroll past, not on every list that is not tiny.
    {
      id: "place",
      kind: "single",
      options: [
        "hongKong", "macau", "mainland", "taiwan", "singapore", "japan",
        "uk", "australia", "canada", "usa", "elsewhere", "noSay",
      ],
    },
    {
      id: "work",
      kind: "single",
      options: [
        "student", "professional", "ownerFounder", "management",
        "education", "healthcare", "socialServices", "creative", "retail",
        "trade", "caregiver", "retired", "other",
      ],
    },
  ],
};


/**
 * The parts Our profile is made of, in order.
 *
 * The section is a shell that renders this list, so the parts still to be
 * built — the setup checklist, the CheckPlus rows, whatever follows — are an
 * entry here and a render function in Profile.js. Nothing about the shell,
 * the view model's shape, or the parts already present has to change to add
 * one, which is the whole reason the section is built this way rather than
 * as a page that happens to contain questions.
 */
// The order the design lays them down the main column. The checklist and the
// notification switches are not here: the design puts them in the rail beside
// the column, which Profile.js renders as part of the shell.
export const PROFILE_PARTS = [
  "account",
  "intro",
  "checkplus",
  "questions",
];

/**
 * What the cover picker may offer, and how big a cover may be.
 *
 * Both halves match what the impact route enforces, and exist so the picker
 * cannot invite a file the server is certain to refuse. It used to: the input
 * said `accept="image/*"` and the drop target tested `/^image\//`, so a WebP,
 * an AVIF or a phone's HEIC could all be chosen, went all the way up, and came
 * back as one generic "did not upload". The size was worse — the modal has
 * always promised "under 5 MB" and nothing on either side enforced it, so a
 * full-size photo failed at whatever gave out first, with no message in it.
 *
 * Kept next to each other on purpose: a type added on the server is a type
 * added here, and a ceiling moved is moved in both. PHOTO_ALLOWED_MIME_TYPES
 * and PHOTO_MAX_BYTES in routes/organizations/impact.js are the other end.
 *
 * Two formats, not the shared photo list's four plus WebP. GIF and BMP were
 * never chosen for a cover — they arrive from config.s3.common, which also
 * covers org logos and payment QR codes — and neither suits a 1600 × 500
 * photograph: an animated GIF is not a cover and a BMP is over the 5 MB
 * ceiling at that size before it is even looked at. JPEG and PNG is also what
 * every other picker in the app offers (MediaGalleryModal, the org logo), so
 * an organisation meets one rule rather than a different one per screen.
 */
export const COVER_IMAGE_TYPES = ["image/jpeg", "image/png"];

export const COVER_MAX_BYTES = 5 * 1024 * 1024;

/**
 * What the logo picker may offer, and how big a logo may be.
 *
 * Same two formats as the cover and as every other picker in the app, and
 * deliberately narrower than what the upload route itself accepts
 * (config.s3.common.photoAllowedExtensions also allows GIF and BMP). An
 * animated GIF is not an organisation's logo, and a BMP at any useful size is
 * over the ceiling before it is looked at — the route stays permissive
 * because org logos, payment QR codes and gallery images all arrive through
 * it, and this is the one screen's rule rather than the route's.
 *
 * The ceiling is the picker's own: POST /organizations/s3 sets no size limit
 * of its own, so without this a full-size phone photo goes all the way up and
 * fails at whatever gives out first. Matching the cover's 5 MB means an
 * organisation meets one rule on this page rather than two.
 */
export const LOGO_IMAGE_TYPES = ["image/jpeg", "image/png"];

export const LOGO_MAX_BYTES = 5 * 1024 * 1024;

/**
 * The short introduction, and the three preferences under it.
 *
 * Both are stored as profile answers rather than in new tables of their own:
 * an introduction is a text answer, and a preference is a single-choice
 * answer with two options. The ids are reserved here so they cannot collide
 * with a question added later.
 */
export const PROFILE_INTRO_ID = "intro";

/**
 * The open "anything else" field under the mission questions. Stored the
 * same way as the introduction, and like it, deliberately outside the six
 * counted questions — the meter is about the set, and an optional field
 * nobody has to fill in would make 100% unreachable in practice.
 */
export const PROFILE_FREEFORM_ID = "freeform";

/**
 * The account rows, and where each reads from. All of it is set up with the
 * rest of the account record — in CheckPlus for the two kinds of
 * organisation — so Plug shows it and points at where it changes rather
 * than offering an edit it does not own.
 *
 * `from` names a source in profileAccountVm's `values`, not a field on any
 * one record: `reg` is organizationIRDNo for both kinds of organisation,
 * which is the field a business's Business Registration number is written to
 * at sign-up as well. The row id differs because the two numbers are not the
 * same thing to the person reading the row.
 */
export const PROFILE_ACCOUNT_FIELDS = {
  nonprofit: [
    { id: "orgName", from: "name" },
    { id: "email", from: "email" },
    { id: "charityNo", from: "reg" },
  ],
  business: [
    { id: "bizName", from: "name" },
    { id: "email", from: "email" },
    { id: "brNo", from: "reg" },
  ],
  // A donor's record, not an organisation's — read from /donors, and with no
  // CheckPlus organisation behind it to send anybody to.
  //
  // The design also draws a "display name" row here, the name a nonprofit
  // sees beside a gift. It is not listed: there is no such field on the
  // account, and a read-only row is the one thing on this card that cannot
  // invent one. It belongs with the introduction — a profile answer — when
  // somebody decides what an anonymous gift should look like.
  individual: [
    { id: "fullName", from: "name" },
    { id: "email", from: "email" },
    { id: "phone", from: "phone" },
    { id: "district", from: "district" },
  ],
};

/**
 * `defaultOn` is what the toggle reads as before anybody touches it, so a
 * preference that is on by default costs no write to stay that way. Only a
 * change is stored, and it is stored explicitly as "on" or "off" rather than
 * by presence — absence has to keep meaning "never decided", or a default
 * could never be changed later without rewriting everyone's answers.
 */
/*
 * The first and third row of every persona's card are commented out, leaving
 * one switch each.
 *
 * The two hidden on each card are the same pair everywhere: an "email me when
 * something happens" and a "send me a monthly summary". Nothing sends either
 * -- grep the ids and the only files that mention them are this catalogue and
 * the three locale packs that label them -- so both were switches promising
 * control over mail that does not exist. The row left standing is the one
 * about what other people see, which is the choice on this card that is at
 * least about something real.
 *
 * In fairness to the next reader: that remaining switch is not wired to
 * anything yet either. It is kept because it is the one worth keeping when it
 * is, not because it works today.
 *
 * Commented rather than deleted, for two reasons. The ids stay reserved, so a
 * preference added later cannot take one and silently inherit the answers
 * already stored under it -- these are ProfileAnswers namespaced by
 * ownerType, and the ones people already toggled are still in the database,
 * unread. And restoring a row is uncommenting a line.
 */
export const PROFILE_PREFS = {
  nonprofit: [
    // { id: "prefMessages", defaultOn: true },
    { id: "prefLeague", defaultOn: true },
    // { id: "prefSummary", defaultOn: true },
  ],
  // Ids are not shared with the nonprofit's even where a switch does the same
  // job, because the answers are namespaced by ownerType and a shared id
  // would make the two indistinguishable in any later read across personas.
  business: [
    // { id: "prefBizUpdates", defaultOn: true },
    { id: "prefBizLeague", defaultOn: true },
    // { id: "prefBizSummary", defaultOn: true },
  ],
  individual: [
    // { id: "prefIndUpdates", defaultOn: true },
    // The one switch on any of these cards that defaults off, and the reason
    // the donor's kept row is this one rather than a league row: it puts a
    // donor's name in front of a nonprofit, so it is opted into rather than
    // out of.
    { id: "prefIndName", defaultOn: false },
    // { id: "prefIndSummary", defaultOn: true },
  ],
};

/**
 * The setup checklist, per persona. Each row names a `signal` that profileVm
 * resolves against what the app already knows — none of it is stored, so the
 * checklist cannot drift from the thing it reports on.
 *
 * `where` marks a row whose work happens outside Plug, which reads as a
 * state rather than as something left undone.
 */
export const PROFILE_CHECKLIST = {
  nonprofit: [
    { id: "details", signal: "orgName" },
    { id: "verified", signal: "section88", where: "checkplus" },
    { id: "cover", signal: "coverAndIntro" },
    { id: "questions", signal: "questions" },
    { id: "firstUpdate", signal: "published" },
  ],
  // The design draws five rows for a business and five for a donor, two of
  // which — "giving rule published" and "storefront badge live" for a
  // business, "payment method added" for a donor — have nothing behind them
  // yet: the rule and the badge are still the mock figures in BIZ_SALES, and
  // Plug never sees a saved card. They are left out rather than drawn as
  // permanent ticks. Every row below resolves from a real fetch, which is
  // the property that makes this list worth reading at all.
  // A signal is the label's key as well as the fact behind it, so two rows
  // reporting the same fact in different words need two signals even when the
  // computation is identical — `bizName` and `orgName` are one read, and
  // `shortlisted` and `following` are another. profileChecklistVm resolves
  // both; what differs is only what the row is called.
  business: [
    { id: "details", signal: "bizName" },
    { id: "registration", signal: "registration", where: "checkplus" },
    { id: "intro", signal: "intro" },
    { id: "questions", signal: "questions" },
    { id: "shortlist", signal: "shortlisted" },
  ],
  individual: [
    { id: "details", signal: "donorName" },
    { id: "questions", signal: "questions" },
    { id: "firstGift", signal: "given" },
    { id: "following", signal: "following" },
  ],
};

/**
 * Nonprofit · the guided tour (CP-308).
 *
 * The tutorial that runs once, on the first sign-in, straight after the
 * profile questions. It walks a new organisation from its dashboard to a
 * donor-ready Impact & proof page by pointing at the controls that get it
 * there, one at a time.
 *
 * Nonprofit only, deliberately. A business and a donor have their own
 * workspaces and their own first jobs, and a tour written against "Our
 * profile" and "Impact & proof" would point at sections neither of them has.
 * When they get a tour it will be a second catalogue here, keyed by persona
 * the way PROFILE_QUESTIONS is — not this one with the steps filtered out.
 *
 * A step is data, not code. Everything that differs between steps is a field
 * below, and GuidedTour renders whatever it is handed:
 *
 *   id        the string key, which is also the locale key under
 *             MARKETPLACE.tour.steps — title, body and the button labels
 *   sec       the workspace section the step is shown on. Arriving at a step
 *             navigates there first, so a step can point at anything in the
 *             workspace without its author having to get the reader there
 *   anchor    the data-tour value of the element to spotlight, or null for a
 *             step that has no target and opens as a centred modal
 *   place     which side of the anchor the tooltip sits on
 *   buttons   the navigation controls the step draws, in display order.
 *             The welcome uses "primary" to begin; the remaining content
 *             steps deliberately expose only "back", "next" and "skip",
 *             because the workspace is inert while the tour is open.
 *             "finish" closes the final step and "later" closes the welcome.
 *
 *             "back" is on every step but the welcome, which has nothing
 *             behind it. Reading order puts it first: Back, then the blue
 *             Next, then the way out.
 *
 * Steps 3–14 of the copy deck are not here yet. They are this same shape:
 * the work each of them needs is a data-tour attribute on the thing it points
 * at, an entry below, and its copy in the three locales.
 */
export const TOUR_STEPS = [
  {
    // The welcome. No target — it is about Plug rather than about any one
    // control, and the design opens it in the middle of the dashboard.
    id: "welcome",
    sec: "dash",
    anchor: null,
    place: "center",
    // Starting the tour is navigation rather than a workspace action. "Maybe
    // later" is used instead of "Skip the tour" because nothing has been
    // shown yet.
    buttons: ["primary", "later"],
  },
  {
    // "Our profile" in the left nav. The nav is on every section, so this
    // step could be shown from anywhere; it stays on the dashboard so the
    // tour reads as moving through the workspace rather than jumping about.
    id: "profile",
    sec: "dash",
    anchor: "nav-profile",
    place: "right",
    buttons: ["back", "next", "skip"],
  },
  {
    // The mission questions, inside Our profile. Advancing from step 2 opens
    // the section this step belongs to before measuring its target.
    id: "mission",
    sec: "profile",
    anchor: "profile-mission",
    // Keep the explanation beside the form. Putting it above a card this tall
    // uses most of the viewport for the tour itself and leaves only a sliver
    // of the questions visible below it.
    place: "right",
    align: "start",
    /*
      The mission card sits below the fold on Our profile, so arriving at this
      step leaves the ring somewhere the reader cannot see. The page already
      knows how to bring that exact card into view -- it is what "Complete my
      profile" asks for -- so the step asks for the same thing rather than the
      tour growing a second way to do it. See `scrollTo` in tourGo.
    */
    scrollTo: "questions",
    /*
      Align the start of the mission card with the viewport. The tour panel is
      top-aligned beside it, so its explanation and the highlighted heading
      are visible together instead of stacked on top of one another.
    */
    scrollBlock: "start",
    buttons: ["back", "next", "skip"],
  },
  {
    // Back to the nav, for the section the rest of the tour lives in. Shown
    // from Our profile, where step 3 left them, so the nav row lights up
    // beside the page they are already on.
    id: "impact",
    sec: "profile",
    anchor: "nav-impact",
    place: "right",
    buttons: ["back", "next", "skip"],
  },
  {
    // The one-sentence introduction. Anchored to the control that opens the
    // editor rather than to the text itself: the spotlight is then pointing
    // at the thing to press, which is what the copy is asking for.
    id: "intro",
    sec: "impact",
    anchor: "proof-intro-edit",
    place: "left",
    tip: true,
    buttons: ["back", "next", "skip"],
  },
  {
    id: "images",
    sec: "impact",
    anchor: "proof-cover-edit",
    place: "bottom",
    buttons: ["back", "next", "skip"],
  },
  {
    id: "goal",
    sec: "impact",
    anchor: "proof-goal-add",
    place: "left",
    buttons: ["back", "next", "skip"],
  },
  {
    /*
      The goal form, which the deck points at as a panel rather than a button.
      A panel only exists while it is open, so this step opens it on arrival —
      see `opens`. Tying that to a hidden workspace action would leave the
      anchor missing when the reader reaches step 8 with "Next".
    */
    id: "goalPanel",
    sec: "impact",
    anchor: "proof-goal-form",
    // The editor spans the content column, leaving no horizontal room for the
    // tour card. Place it below so every goal field remains visible.
    place: "bottom",
    opens: "goal",
    tip: true,
    buttons: ["back", "next", "skip"],
  },
  {
    id: "metric",
    sec: "impact",
    anchor: "proof-metric-add",
    place: "left",
    buttons: ["back", "next", "skip"],
  },
  {
    id: "metricPanel",
    sec: "impact",
    anchor: "proof-metric-form",
    // The form reaches far enough down the viewport that a bottom card is
    // clamped back over its upload row. There is clear space above it, so the
    // card belongs there and leaves the complete editor unobstructed. Align
    // the editor's end with the viewport so the card has enough room above;
    // centring a target this tall still made the two edges touch.
    place: "top",
    scrollBlock: "end",
    opens: "metric",
    tip: true,
    buttons: ["back", "next", "skip"],
  },
  {
    // The deck's target here is the "+ Add what a giving buys" button, not the
    // panel behind it — so no `opens`, unlike the two steps above.
    id: "buys",
    sec: "impact",
    anchor: "proof-buy-add",
    place: "left",
    tip: true,
    buttons: ["back", "next", "skip"],
  },
  {
    // "Preview public page" is the section head's own secondary button, which
    // is why the anchor is on the page head rather than on anything inside
    // Impact & proof.
    id: "preview",
    sec: "impact",
    anchor: "page-preview",
    place: "bottom",
    buttons: ["back", "next", "skip"],
  },
  {
    /*
      The closing screen. Centred, like the welcome, and for the same reason:
      it is about the whole run rather than about a control.

      Not the last step, which is what the deck's own button labels settle —
      its navigation is "Next", which needs something after it, and the step
      that follows is the one offering "Finish the tour". The two rows share the
      number 13 in the deck; this is the first of them.
    */
    id: "done",
    sec: "impact",
    anchor: null,
    place: "center",
    buttons: ["back", "next", "skip"],
  },
  {
    /*
      Verification and payouts, which Plug shows and CheckPlus owns. Last,
      It ends the run without offering an external action while the tour is
      active; the highlighted CheckPlus controls remain available afterward.
    */
    id: "checkplus",
    sec: "profile",
    anchor: "profile-checkplus",
    place: "right",
    buttons: ["back", "finish"],
  },
];

/**
 * Which steps carry an always-on (i) button, by id.
 *
 * Derived from the catalogue rather than listed beside it. The deck marks the
 * always-on column on the step itself, so `tip: true` above is the same fact
 * in the same place, and a second list could only ever disagree with it.
 *
 * An (i) opens that step's own panel, wording and all, with nothing but a
 * close button on it. That is the whole point of the deck's column: the four
 * fields it marks are the ones whose guidance is worth having after the tour
 * has been and gone, and repeating the copy somewhere else would be two
 * things to keep saying the same thing.
 */
export const TOUR_TIPS = TOUR_STEPS.filter((step) => step.tip).map(
  (step) => step.id
);

/**
 * The rows on the profile that Plug shows but does not own. Both are set up
 * and changed in CheckPlus, and Plug only reads the verified result — saying
 * so on the page is what stops an organisation looking for a control here
 * that was never going to be here.
 *
 * Keyed by persona so the card disappears rather than misinforms: Section 88
 * and a nonprofit payout account are a nonprofit's, and a business or a donor
 * shown either would be reading about somebody else's account. An absent
 * persona leaves the part with no rows, which Profile.js already draws as
 * nothing at all.
 */
export const PROFILE_CHECKPLUS_ROWS = {
  nonprofit: ["section88", "payout"],
};

// Individual · my dashboard. Every gift made through Plug, whatever route it
// came in by, so a donor can find a receipt without asking a nonprofit.
export const DONOR_HISTORY = [
  { id: "aug", who: "Monthly plan · 3 nonprofits", kind: "recurring", amount: 200 },
  { id: "jul22", who: "Mid-autumn mooncake box · Lung Cheong Bakery", kind: "purchase", amount: 268 },
  { id: "jul", who: "Monthly plan · 3 nonprofits", kind: "recurring", amount: 200 },
  { id: "jun14", who: "Harbour Shelter Trust · shelter reopening", kind: "once", amount: 500 },
  { id: "jun", who: "Monthly plan · 3 nonprofits", kind: "recurring", amount: 200 },
  { id: "may", who: "Monthly plan · 2 nonprofits", kind: "recurring", amount: 150 },
];

// Where a donor's giving has gone since they joined, largest share first.
// `projects` splits each nonprofit's total across what it funded, and must sum
// to that total — the same shape realMyDash derives from live payments.
export const DONOR_ALLTIME = [
  {
    id: "shelter",
    total: 2940,
    pct: 43,
    projects: [
      { id: "shelter-beds", amount: 1740 },
      { id: "shelter-outreach", amount: 1200 },
    ],
  },
  {
    id: "food",
    total: 2390,
    pct: 35,
    projects: [
      { id: "food-meals", amount: 1490 },
      { id: "food-pantry", amount: 900 },
    ],
  },
  {
    id: "quiet",
    total: 1510,
    pct: 22,
    projects: [{ id: "quiet-sessions", amount: 1510 }],
  },
];

// The same totals grouped by cause rather than by nonprofit.
export const DONOR_CAUSES = [
  { id: "homelessness", pct: 43, amount: 2940 },
  { id: "food", pct: 35, amount: 2390 },
  { id: "mental", pct: 22, amount: 1510 },
];

export function fmt(amount) {
  return "HK$" + Math.round(amount || 0).toLocaleString("en-US");
}

// Initials for an avatar tile. Words without a letter in them (&, -, ·) are
// dropped so "Mor & Chan Accountants" reads MC; everything else counts, which
// matters for the many two-letter Hong Kong surnames — "Wing Ho" is WH, not W.
export function mono(name) {
  if (!name) {
    return "";
  }
  const initials = (name || "")
    .split(/\s+/)
    .filter((word) => /[a-z]/i.test(word))
    .slice(0, 2)
    .map((word) => word.replace(/[^a-z]/gi, "")[0].toUpperCase());
  return initials.join("") || name.trim().slice(0, 2).toUpperCase();
}
